import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiUrl, authApi } from '../services/api';
import { toErrorMessage } from '../utils/errorUtils';

interface User {
  user_id: number;
  email: string;
  username: string;
  first_name: string;
  last_name: string;
  company_name?: string;
  phone?: string;
  address?: string;
  city?: string;
  postal_code?: string;
  country?: string;
  role: 'admin' | 'customer' | 'provider' | 'production';
  created_at: string;
  last_login?: string;
  provider_markup_percentage?: number;
  provider_code?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (email: string, password: string) => Promise<void>;
  requestLoginCode: (email: string) => Promise<{ message: string; testMode?: boolean; devCode?: string }>;
  loginWithCode: (email: string, code: string) => Promise<void>;
  register: (userData: RegisterData) => Promise<void>;
  logout: () => void;
  updateProfile: (userData: Partial<User>) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  loading: boolean;
  isAuthenticated: boolean;
  isAdmin: boolean;
}

interface RegisterData {
  email: string;
  password: string;
  username: string;
  first_name: string;
  last_name: string;
  company_name?: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

const mapAuthUser = (userData: any): User => ({
  user_id: userData.id ?? userData.user_id,
  email: userData.email,
  username: userData.username,
  first_name: userData.first_name,
  last_name: userData.last_name,
  company_name: userData.company_name,
  phone: userData.phone,
  address: userData.address,
  city: userData.city,
  postal_code: userData.postal_code,
  country: userData.country,
  role: userData.role as 'admin' | 'customer' | 'provider' | 'production',
  created_at: userData.created_at || new Date().toISOString(),
  last_login: userData.last_login,
  provider_markup_percentage:
    userData.provider_markup_percentage !== undefined && userData.provider_markup_percentage !== null
      ? Number(userData.provider_markup_percentage)
      : undefined,
  provider_code: userData.provider_code,
});

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('authToken');
      if (storedToken) {
        try {
          setToken(storedToken);
          // Use the users API endpoint to get complete profile data
          const response = await fetch(apiUrl('/users/profile'), {
            headers: {
              'Authorization': `Bearer ${storedToken}`
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            // Backend returns user data wrapped in a 'user' property
            setUser(mapAuthUser(data.user));
          } else {
            throw new Error('Failed to fetch profile');
          }
        } catch (error) {
          console.error('Failed to restore auth state:', error);
          localStorage.removeItem('authToken');
          setToken(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email: string, password: string) => {
    try {
      const response = await authApi.login(email, password);
      const { user: userData, token: authToken } = response.data;
      
      setUser(mapAuthUser(userData));
      setToken(authToken);
      localStorage.setItem('authToken', authToken);
    } catch (error: any) {
      throw new Error(toErrorMessage(error, 'Login failed'));
    }
  };

  const requestLoginCode = async (email: string) => {
    try {
      const response = await authApi.requestLoginCode(email);
      return {
        message: response.data.message,
        testMode: response.data.test_mode,
        devCode: response.data.dev_code,
      };
    } catch (error: any) {
      const err = new Error(toErrorMessage(error, 'Failed to send login code')) as Error & {
        response?: any;
        status?: number;
      };
      err.response = error?.response;
      err.status = error?.response?.status;
      throw err;
    }
  };

  const loginWithCode = async (email: string, code: string) => {
    try {
      const response = await authApi.verifyLoginCode(email, code);
      const { user: userData, token: authToken } = response.data;

      setUser(mapAuthUser(userData));
      setToken(authToken);
      localStorage.setItem('authToken', authToken);
    } catch (error: any) {
      throw new Error(toErrorMessage(error, 'Code verification failed'));
    }
  };

  const register = async (userData: RegisterData) => {
    try {
      const response = await authApi.register(userData);
      const { user: newUser, token: authToken } = response.data;
      
      setUser(mapAuthUser(newUser));
      setToken(authToken);
      localStorage.setItem('authToken', authToken);
    } catch (error: any) {
      throw new Error(toErrorMessage(error, 'Registration failed'));
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('authToken');
  };

  const updateProfile = async (userData: Partial<User>) => {
    try {
      const response = await fetch(apiUrl('/users/profile'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(userData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(toErrorMessage(errorData, 'Profile update failed'));
      }

      const result = await response.json();
      // Backend may return { user } or the user object directly
      const updated = result.user || result;
      setUser(mapAuthUser(updated));
      return updated;
    } catch (error: any) {
      throw new Error(toErrorMessage(error, 'Profile update failed'));
    }
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    try {
      await authApi.changePassword(currentPassword, newPassword);
    } catch (error: any) {
      throw new Error(toErrorMessage(error, 'Password change failed'));
    }
  };

  const value: AuthContextType = {
    user,
    token,
    login,
    requestLoginCode,
    loginWithCode,
    register,
    logout,
    updateProfile,
    changePassword,
    loading,
    isAuthenticated: !!user && !!token,
    isAdmin: user?.role === 'admin'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
